import React, { useEffect, useState } from 'react'
import { Box, Button, Card, CardContent, CardMedia, CircularProgress, Grid, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { Navbar } from '../features/navigation/components/Navbar'
import { Footer } from '../features/footer/Footer'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import { useDispatch, useSelector } from 'react-redux'
import { addToCartAsync } from '../features/cart/CartSlice'
import { selectLoggedInUser } from '../features/auth/AuthSlice'
import { motion } from 'framer-motion'
import { fetchTestersAsync, selectTesters } from '../features/testers/TesterSlice'

export const TestersPage = () => {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const testers = useSelector(selectTesters)
  const dispatch = useDispatch()
  const loggedInUser = useSelector(selectLoggedInUser)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchTesters = async () => {
      try {
        setLoading(true)
        await dispatch(fetchTestersAsync({ page: 1, limit: 20 }))
      } finally {
        setLoading(false)
      }
    }
    fetchTesters()
  }, [dispatch])

  const handleAddToCart = (tester) => {
    dispatch(addToCartAsync({
      user: loggedInUser?._id,
      product: tester._id,
      productData: {
        _id: tester._id,
        title: tester.name,
        price: tester.price,
        images: [tester.image],
        thumbnail: tester.image,
        brand: { name: tester.brand },
        stockQuantity: tester.inStock ? 999 : 0
      },
      quantity: 1
    }))
  }

  return (
    <>
      <Navbar />
      
      <Box sx={{ minHeight: '80vh', py: 6, px: isMobile ? 2 : 4, backgroundColor: '#f9f9f9' }}>
        <Stack spacing={4}>
          {/* Header */}
          <Stack spacing={2} textAlign="center">
            <Typography 
              variant="h2" 
              sx={{
                fontWeight: 900,
                fontSize: isMobile ? '2.15rem' : '3.25rem',
                color: 'primary.main',
                lineHeight: 1.1
              }}
            >
              Perfume Testers
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Try before you buy - Our exclusive perfume testers help you find your perfect scent
            </Typography>
          </Stack>

          {/* Loading */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : testers.length > 0 ? (
            <Grid container spacing={3}>
              {testers.map((tester, idx) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={tester._id}>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'all 0.3s',
                        '&:hover': {
                          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.15)',
                          transform: 'translateY(-8px)'
                        }
                      }}
                    >
                      {/* Image */}
                      <CardMedia
                        component="img"
                        height="250"
                        image={tester.image}
                        alt={tester.name}
                        sx={{ objectFit: 'cover' }}
                      />

                      {/* Content */}
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Stack spacing={1.5}>
                          {/* Brand & Volume */}
                          <Box>
                            <Typography variant="caption" color="primary">
                              {tester.brand}
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {tester.name}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              Volume: {tester.volume}
                            </Typography>
                          </Box>

                          {/* Description */}
                          <Typography 
                            variant="body2" 
                            sx={{
                              color: 'text.secondary',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical'
                            }}
                          >
                            {tester.description}
                          </Typography>

                          {/* Stock Status */}
                          <Typography 
                            variant="caption"
                            sx={{
                              color: tester.inStock ? 'success.main' : 'error.main',
                              fontWeight: 600
                            }}
                          >
                            {tester.inStock ? '✓ In Stock' : '✗ Out of Stock'}
                          </Typography>

                          {/* Price */}
                          <Box sx={{ pt: 1, borderTop: '1px solid #e0e0e0' }}>
                            <Typography 
                              variant="h6" 
                              sx={{
                                fontWeight: 700,
                                color: 'primary.main'
                              }}
                            >
                              PKR {tester.price.toLocaleString()}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>

                      {/* Add to Cart Button */}
                      <Box sx={{ p: 2, pt: 0 }}>
                        <Button
                          fullWidth
                          variant="contained"
                          startIcon={<ShoppingCartIcon />}
                          disabled={!tester.inStock}
                          onClick={() => handleAddToCart(tester)}
                          sx={{
                            fontWeight: 600
                          }}
                        >
                          {tester.inStock ? 'Add to Cart' : 'Out of Stock'}
                        </Button>
                      </Box>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="textSecondary">
                No testers available at the moment
              </Typography>
            </Box>
          )}
        </Stack>
      </Box>

      <Footer />
    </>
  )
}
